import { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TAROT_DECK, drawCards } from "@/data/tarot_deck";
import html2canvas from "html2canvas";
import { useLanguage } from "../contexts/LanguageContext";
import GuestUpgradePrompt from "@/components/GuestUpgradePrompt";

// Roman numerals for Major Arcana
const MAJOR_NUMERALS = {
  "The Fool": "0", "The Magician": "I", "The High Priestess": "II", "The Empress": "III",
  "The Emperor": "IV", "The Hierophant": "V", "The Lovers": "VI", "The Chariot": "VII",
  "Strength": "VIII", "The Hermit": "IX", "Wheel of Fortune": "X", "Justice": "XI",
  "The Hanged Man": "XII", "Death": "XIII", "Temperance": "XIV", "The Devil": "XV",
  "The Tower": "XVI", "The Star": "XVII", "The Moon": "XVIII", "The Sun": "XIX",
  "Judgement": "XX", "The World": "XXI"
};

// Helper function to translate card name
const translateCardName = (cardName, t) => {
  // Major Arcana translations
  const majorArcana = {
    "The Fool": "the_fool",
    "The Magician": "the_magician",
    "The High Priestess": "the_high_priestess",
    "The Empress": "the_empress",
    "The Emperor": "the_emperor",
    "The Hierophant": "the_hierophant",
    "The Lovers": "the_lovers",
    "The Chariot": "the_chariot",
    "Strength": "strength",
    "The Hermit": "the_hermit",
    "Wheel of Fortune": "wheel_of_fortune",
    "Justice": "justice",
    "The Hanged Man": "the_hanged_man",
    "Death": "death",
    "Temperance": "temperance",
    "The Devil": "the_devil",
    "The Tower": "the_tower",
    "The Star": "the_star",
    "The Moon": "the_moon",
    "The Sun": "the_sun",
    "Judgement": "judgement",
    "The World": "the_world"
  };
  
  // Check if Major Arcana
  if (majorArcana[cardName]) {
    return t(`tarot_cards.${majorArcana[cardName]}`);
  }
  
  // Minor Arcana parsing (e.g., "Two of Cups" -> "Dos de Copas")
  const minorMatch = cardName.match(/^(Ace|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|Page|Knight|Queen|King) of (Wands|Cups|Swords|Pentacles)$/);
  if (minorMatch) {
    const rankMap = {
      "Ace": "ace_of", "Two": "two_of", "Three": "three_of", "Four": "four_of",
      "Five": "five_of", "Six": "six_of", "Seven": "seven_of", "Eight": "eight_of",
      "Nine": "nine_of", "Ten": "ten_of", "Page": "page_of", "Knight": "knight_of",
      "Queen": "queen_of", "King": "king_of"
    };
    const suitMap = {
      "Wands": "wands", "Cups": "cups", "Swords": "swords", "Pentacles": "pentacles"
    };
    const rank = t(`tarot_cards.${rankMap[minorMatch[1]]}`);
    const suit = t(`tarot_cards.${suitMap[minorMatch[2]]}`);
    return `${rank} ${suit}`;
  }
  
  return cardName; // Fallback to original name
};

// Elegant card face component - Now with AI-generated luxury images
const TarotCardFace = ({ card, t }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  return (
    <div className="absolute inset-0 rounded-lg overflow-hidden" style={{ 
      background: "linear-gradient(165deg, #0a0a0a 0%, #151515 50%, #0d0d0d 100%)",
      border: "2px solid #D4AF37",
      boxShadow: "inset 0 0 20px rgba(212, 175, 55, 0.1), 0 4px 12px rgba(0,0,0,0.5)"
    }}>
      {/* Card Image */}
      {card.image && !imageError ? (
        <>
          <img 
            src={card.image} 
            alt={card.name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
          {/* Loading placeholder */}
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-primary/30 text-2xl animate-pulse">✧</div>
            </div>
          )}
          {/* Card name overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2">
            <div className="text-primary text-[9px] sm:text-[11px] font-cinzel uppercase tracking-wide text-center" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>
              {translateCardName(card.name, t)}
            </div>
          </div>
        </>
      ) : (
        /* Fallback design if no image */
        <>
          <div className="absolute inset-1.5 border border-primary/30 rounded" />
          <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
            <div className="text-primary/30 text-3xl mb-2">✧</div>
            <div className="text-primary text-[8px] sm:text-[10px] font-cinzel uppercase tracking-wide text-center">
              {translateCardName(card.name, t)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Card back design - Now with AI-generated luxury image
const TarotCardBack = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const cardBackUrl = "https://storage.googleapis.com/astral-belonging/tarot-deck/major/card_back.png";
  
  return (
    <div className="absolute inset-0 rounded-lg overflow-hidden" style={{ 
      background: "linear-gradient(165deg, #0a0a0a 0%, #121212 50%, #0a0a0a 100%)",
      border: "2px solid rgba(212, 175, 55, 0.4)"
    }}>
      {!imageError ? (
        <>
          <img 
            src={cardBackUrl} 
            alt="Card Back"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-primary/30 text-2xl sm:text-3xl animate-pulse">✧</div>
            </div>
          )}
        </>
      ) : (
        /* Fallback design */
        <>
          <div className="absolute inset-1.5 border border-primary/20 rounded" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-primary/30 text-2xl sm:text-3xl">✧</div>
          </div>
          <div className="absolute inset-3 border border-primary/10 rounded opacity-50" />
        </>
      )}
    </div>
  );
};

const TarotReader = ({ 
  userName, 
  onComplete, 
  onBack,
  // Enhanced personalization props
  userContext = {}, // { gender, birthDate, birthTime, birthLocation, mood, focus, sunSign, moonSign, risingSign }
  // Guest upgrade props
  isGuest = false,
  onGuestUpgrade = null
}) => {
  const { t, language } = useLanguage();
  const positions = [t('tarot.past'), t('tarot.present'), t('tarot.future')];
  
  // Map language code to full language name for API
  const languageNames = {
    en: 'English', es: 'Spanish', zh: 'Chinese', hi: 'Hindi', 
    nl: 'Dutch', ja: 'Japanese', ar: 'Arabic'
  };
  const targetLanguage = languageNames[language] || 'English';
  
  const [phase, setPhase] = useState("deck");
  const [drawnCards, setDrawnCards] = useState([]);
  const [revealedCards, setRevealedCards] = useState([0, 0, 0]);
  const [reading, setReading] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const readingRef = useRef(null);

  // Celestial Invitation - Generate and download branded image
  const handleShare = async () => {
    if (!readingRef.current || isSharing) return;
    
    setIsSharing(true);
    try {
      const canvas = await html2canvas(readingRef.current, {
        backgroundColor: "#0a0a0f",
        scale: 2,
        logging: false,
        useCORS: true,
      });
      
      const link = document.createElement("a");
      link.download = `tarot-reading-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      
      if (navigator.vibrate) navigator.vibrate(100);
    } catch (err) {
      console.error("Share failed:", err);
    }
    setIsSharing(false);
  };

  const handleShuffle = useCallback(() => {
    setPhase("shuffling");
    if (navigator.vibrate) navigator.vibrate([50, 30, 50]);
    setTimeout(() => {
      setDrawnCards(drawCards(3));
      setPhase("drawing");
    }, 2000);
  }, []);

  const getReading = async (cards) => {
    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Extract location string from location object if needed
      const locationValue = userContext.location || userContext.birthLocation;
      const locationString = locationValue?.display || (typeof locationValue === 'string' ? locationValue : null);
      
      const response = await fetch(backendUrl + "/api/tarot/reading", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cards: cards.map((c, i) => ({ name: c.name, meaning: c.meaning, position: positions[i] })),
          user_name: userName || t('child_of_stars'),
          language: targetLanguage,
          // Enhanced personalization data
          gender: userContext.gender || userContext.genderIdentity || null,
          birth_date: userContext.birthDate || null,
          birth_time: userContext.birthTime || null,
          birth_location: locationString,
          mood: userContext.mood || null,
          sacred_purpose: userContext.focus || userContext.sacredPurpose || null,
          sun_sign: userContext.sunSign || null,
          moon_sign: userContext.moonSign || null,
          rising_sign: userContext.risingSign || null
        })
      });
      if (response.ok) {
        const data = await response.json();
        setReading(data);
        
        // Track reading count for guests and trigger upgrade prompt
        if (isGuest && onGuestUpgrade) {
          const readingCount = parseInt(localStorage.getItem('guest_reading_count') || '0') + 1;
          localStorage.setItem('guest_reading_count', String(readingCount));
          
          // Show upgrade prompt on 2nd reading
          if (readingCount >= 2) {
            setTimeout(() => setShowUpgradePrompt(true), 3000); // Delay to let them see the reading first
          }
        }
      } else {
        setReading({ mantra: t('tarot.fallback_mantra'), path: [t('tarot.fallback_path_1'), t('tarot.fallback_path_2'), t('tarot.fallback_path_3')] });
      }
    } catch (err) {
      setReading({ mantra: t('tarot.error_mantra'), path: [t('tarot.error_path_1'), t('tarot.error_path_2'), t('tarot.error_path_3')] });
    }
    setIsLoading(false);
    setPhase("reading");
  };

  const handleDraw = useCallback(() => {
    setPhase("revealing");
    setTimeout(() => setRevealedCards([1, 0, 0]), 500);
    setTimeout(() => setRevealedCards([1, 1, 0]), 1500);
    setTimeout(() => {
      setRevealedCards([1, 1, 1]);
      getReading(drawnCards);
    }, 2500);
  }, [drawnCards]);

  const handleReset = () => {
    setPhase("deck");
    setDrawnCards([]);
    setRevealedCards([0, 0, 0]);
    setReading(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex flex-col items-center justify-center p-4">
      {onBack && (
        <button onClick={onBack} className="absolute top-4 left-4 text-primary/60 hover:text-primary" data-testid="tarot-back-btn">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <motion.h1 className="text-2xl sm:text-3xl font-cinzel text-primary mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        ASTRAL BELONGING
      </motion.h1>

      {phase === "deck" && (
        <motion.div className="flex flex-col items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <p className="text-primary/60 text-sm mb-6 font-inter">{userName || t('child_of_stars')}, {t('ui.the_cards_await')}</p>
          <motion.div className="relative w-32 h-48 cursor-pointer" onClick={handleShuffle} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="tarot-deck">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="absolute inset-0 rounded-lg overflow-hidden" style={{ transform: "translateY(" + (-i * 2) + "px)", zIndex: 5 - i }}>
                <img 
                  src="https://storage.googleapis.com/astral-belonging/tarot-deck/major/card_back.png" 
                  alt="Card Back"
                  className="w-full h-full object-cover rounded-lg"
                  style={{ border: "2px solid rgba(212, 175, 55, 0.4)" }}
                />
              </div>
            ))}
          </motion.div>
          <p className="text-primary/40 text-xs mt-6 font-inter">{t('ui.tap_to_shuffle')}</p>
        </motion.div>
      )}

      {phase === "shuffling" && (
        <motion.div className="flex flex-col items-center">
          <p className="text-primary/60 text-sm mb-6 font-inter">{t('tarot.cards_are_speaking')}</p>
          <div className="relative w-32 h-48">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <motion.div key={i} className="absolute inset-0 rounded-lg overflow-hidden"
                animate={{ x: [0, i % 2 === 0 ? 30 : -30, 0], y: [0, -10, 0] }}
                transition={{ duration: 0.3, repeat: 6, delay: i * 0.05 }}>
                <img 
                  src="https://storage.googleapis.com/astral-belonging/tarot-deck/major/card_back.png" 
                  alt="Card Back"
                  className="w-full h-full object-cover rounded-lg"
                  style={{ border: "2px solid rgba(212, 175, 55, 0.4)" }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {phase === "drawing" && (
        <motion.div className="flex flex-col items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <p className="text-primary/60 text-sm mb-6 font-inter">{t('tarot.draw_three_cards')}</p>
          <div className="flex gap-4">
            {positions.map((pos, i) => (
              <motion.div key={pos} className="flex flex-col items-center" initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.2 }}>
                <span className="text-primary/40 text-xs mb-2 font-inter">{pos}</span>
                <div className="w-20 h-32 rounded-lg overflow-hidden" style={{ border: "2px solid rgba(212, 175, 55, 0.4)" }}>
                  <img 
                    src="https://storage.googleapis.com/astral-belonging/tarot-deck/major/card_back.png" 
                    alt="Card Back"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            ))}
          </div>
          <motion.button onClick={handleDraw} className="mt-8 px-8 py-3 border border-primary/50 rounded font-cinzel text-primary hover:bg-primary/10" data-testid="tarot-draw-btn">
            {t('ui.reveal_cards').toUpperCase()}
          </motion.button>
        </motion.div>
      )}

      {(phase === "revealing" || phase === "reading") && (
        <motion.div className="flex flex-col items-center w-full max-w-2xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex gap-3 sm:gap-6 mb-8">
            {drawnCards.map((card, i) => (
              <motion.div key={card.id} className="flex flex-col items-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.3 }}>
                <span className="text-primary/40 text-xs mb-2 font-inter">{positions[i]}</span>
                <div className="relative w-20 h-32 sm:w-28 sm:h-44" style={{ perspective: 1000 }}>
                  <motion.div className="absolute inset-0" animate={{ rotateY: revealedCards[i] ? 180 : 0 }} transition={{ duration: 0.6 }} style={{ transformStyle: "preserve-3d" }}>
                    {/* Card Back */}
                    <div className="absolute inset-0" style={{ backfaceVisibility: "hidden" }}>
                      <TarotCardBack />
                    </div>
                    {/* Card Face */}
                    <div className="absolute inset-0" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                      <TarotCardFace card={card} t={t} />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          {isLoading && (
            <motion.div className="text-primary/60 text-sm font-inter" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 1.5 }}>
              {t('tarot.oracle_interprets')}
            </motion.div>
          )}

          <AnimatePresence>
            {reading && phase === "reading" && (
              <motion.div className="w-full max-w-lg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {/* Shareable Content - Captured by html2canvas */}
                <div ref={readingRef} className="bg-gradient-to-b from-gray-900 via-black to-gray-900 border border-primary/20 rounded-lg p-6">
                  {/* Branding Header */}
                  <div className="text-center mb-4 pb-4 border-b border-primary/10">
                    <span className="text-primary/30 text-xs font-cinzel tracking-[0.2em] uppercase">Astral Belonging</span>
                    <div className="text-primary/50 text-[10px] mt-1 font-inter">{t('tarot.tarot_of_void')}</div>
                  </div>

                  {/* Mini Card Display with actual images */}
                  <div className="flex justify-center gap-3 mb-4">
                    {drawnCards.map((card, i) => {
                      return (
                        <div key={card.id} className="text-center">
                          <div className="w-14 h-20 rounded border border-primary/50 overflow-hidden" style={{ background: "linear-gradient(165deg, #0a0a0a 0%, #151515 100%)" }}>
                            {card.image ? (
                              <img 
                                src={card.image} 
                                alt={card.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-primary/60 text-xs">✧</span>
                              </div>
                            )}
                          </div>
                          <span className="text-primary/40 text-[8px] block font-inter mt-1">{positions[i]}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* The Reading */}
                  <p className="text-primary font-cinzel text-lg sm:text-xl text-center mb-6 leading-relaxed">{reading.mantra}</p>
                  
                  <div className="border-t border-primary/20 pt-4">
                    <h3 className="text-primary/60 text-xs font-inter uppercase tracking-wider mb-3">{t('tarot.the_path')}</h3>
                    <ul className="space-y-3">
                      {reading.path.map((step, i) => (
                        <motion.li key={i} className="flex gap-3 text-primary/80 text-sm font-inter" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.2 }}>
                          <span className="text-primary/40">{i + 1}.</span>
                          <span>{step}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Footer Branding */}
                  <div className="text-center mt-6 pt-4 border-t border-primary/10">
                    <span className="text-primary/20 text-[9px] font-inter">✧ {t('tarot.stars_have_spoken')} ✧</span>
                  </div>
                </div>

                {/* Action Buttons (not captured in screenshot) */}
                <div className="flex gap-3 mt-6 justify-center">
                  <motion.button 
                    onClick={handleShare} 
                    disabled={isSharing}
                    className="px-4 py-2 border border-primary/30 rounded text-primary/60 text-sm font-inter hover:bg-primary/10 flex items-center gap-2 disabled:opacity-50" 
                    data-testid="tarot-share-btn"
                  >
                    {isSharing ? (
                      <span className="animate-spin">◌</span>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    )}
                    {t('ui.share')}
                  </motion.button>
                  <motion.button onClick={handleReset} className="px-4 py-2 border border-primary/30 rounded text-primary/60 text-sm font-inter hover:bg-primary/10" data-testid="tarot-reset-btn">
                    {t('ui.reset')}
                  </motion.button>
                  {onComplete && (
                    <motion.button onClick={() => onComplete(drawnCards, reading)} className="px-4 py-2 bg-primary/20 border border-primary rounded text-primary text-sm font-inter hover:bg-primary/30" data-testid="tarot-complete-btn">
                      {t('ui.continue')}
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      
      {/* Guest Upgrade Prompt - shown after 2nd reading */}
      <GuestUpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        onSuccess={(userData) => {
          setShowUpgradePrompt(false);
          if (onGuestUpgrade) onGuestUpgrade(userData);
        }}
        trigger="reading"
      />
    </div>
  );
};

export default TarotReader;
